import * as React from 'react';
import { ApolloError } from 'apollo-client';
import { Query } from 'react-apollo';
import moment from 'moment-timezone';

import CoachingConstants from 'coaching/CoachingConstants';
import GET_AVAILABLE_RESERVATIONS_QUERY from 'coaching/queries/GetAvailableReservations.gql';
import { GetAvailableReservations } from 'coaching/queries/__generated__/GetAvailableReservations';

export type CoachingReservation = {
  coachUuid: string;
  startDateTime: moment.Moment;
  endDateTime: moment.Moment;
};

export type SavedCoachingReservation = CoachingReservation & {
  reservationUuid: string;
  expiresAt: string;
};

export type CoachingTimeOff = {
  startDate: moment.Moment;
  endDate: moment.Moment;
};

export type Props = {
  subject: string;
  startDateTimeRange: string;
  endDateTimeRange: string;
  children: (props: {
    reservations?: CoachingReservation[];
    timeOff?: CoachingTimeOff[];
    loading: boolean;
    error?: ApolloError;
  }) => React.ReactNode;
};

export default function GetAvailableReservationsContainer(props: Props) {
  // take Pacific time (in DST or Standard) and convert to UTC
  const startOfBusinessHoursInUTC = moment
    .utc(
      moment
        .tz('America/Los_Angeles')
        .set('hour', CoachingConstants.START_OF_COACHING_HOURS_IN_PACIFIC)
    )
    .hour();
  // end is non-inclusive for dates returned from backend (i.e. a 5PM cap is "no sessions start on or after 5")
  const endOfBusinessHoursInUTC = moment
    .utc(
      moment
        .tz('America/Los_Angeles')
        .set('hour', CoachingConstants.END_OF_COACHING_HOURS_IN_PACIFIC)
    )
    .hour();
  return (
    <Query<GetAvailableReservations>
      query={GET_AVAILABLE_RESERVATIONS_QUERY}
      variables={{
        subject: props.subject,
        startDateTimeRange: props.startDateTimeRange,
        endDateTimeRange: props.endDateTimeRange,
        startOfBusinessHoursInUTC,
        endOfBusinessHoursInUTC,
      }}>
      {({ loading, error, data }) => {
        return props.children({
          reservations: data?.coaching?.getPossibleCoachingSessions.availableSessions.map(res => ({
            coachUuid: res.coachUuid,
            startDateTime: moment(res.startDateTime),
            endDateTime: moment(res.endDateTime),
          })),
          timeOff: data?.coaching?.getPossibleCoachingSessions.timeOff.map(res => ({
            startDate: moment(res.startDate),
            endDate: moment(res.endDate),
          })),
          loading,
          error,
        });
      }}
    </Query>
  );
}
